import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { whitelistApi } from '../api/whitelist';

export const useWhitelist = (guildId: string | null) => {
  return useQuery({
    queryKey: ['whitelist', guildId],
    queryFn: () => whitelistApi.getWhitelist(guildId!),
    enabled: !!guildId,
  });
};

export const useAddWhitelistEntry = (guildId: string | null) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ type, id }: { type: 'channels' | 'roles' | 'users'; id: string }) =>
      whitelistApi.addWhitelistEntry(guildId!, type, id),
    onMutate: async ({ type, id }) => {
      await queryClient.cancelQueries({ queryKey: ['whitelist', guildId] });

      const previousWhitelist = queryClient.getQueryData(['whitelist', guildId]);

      // 낙관적 업데이트 - 새 항목 추가
      queryClient.setQueryData(['whitelist', guildId], (old: any) => {
        if (!old) return old;
        return {
          ...old,
          [type]: [
            ...old[type],
            {
              id,
              name: 'Loading...', // 실제 이름은 서버에서 받아옴
            },
          ],
        };
      });

      return { previousWhitelist };
    },
    onError: (_err, _variables, context) => {
      if (context?.previousWhitelist) {
        queryClient.setQueryData(['whitelist', guildId], context.previousWhitelist);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['whitelist', guildId] });
    },
  });
};

export const useRemoveWhitelistEntry = (guildId: string | null) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ type, id }: { type: 'channels' | 'roles' | 'users'; id: string }) =>
      whitelistApi.removeWhitelistEntry(guildId!, type, id),
    onMutate: async ({ type, id }) => {
      await queryClient.cancelQueries({ queryKey: ['whitelist', guildId] });

      const previousWhitelist = queryClient.getQueryData(['whitelist', guildId]);

      // 낙관적 업데이트 - 항목 제거
      queryClient.setQueryData(['whitelist', guildId], (old: any) => {
        if (!old) return old;
        return {
          ...old,
          [type]: old[type].filter((entry: any) => entry.id !== id),
        };
      });

      return { previousWhitelist };
    },
    onError: (_err, _variables, context) => {
      if (context?.previousWhitelist) {
        queryClient.setQueryData(['whitelist', guildId], context.previousWhitelist);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['whitelist', guildId] });
    },
  });
};
